import { useMemo, useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { RefreshCw, Zap, Settings, ExternalLink, Search, X, ChevronDown, ChevronRight, Archive } from 'lucide-react';
import { groupByCategory, CATEGORY_ORDER, CATEGORY_CONFIG, getRunTemplateName } from '../lib/workflow-categories';
import { Button } from '../components/ui/Button';
import { apiClient } from '../lib/api-client';
import { WorkflowCard } from '../components/workflows/WorkflowCard';
import { WorkflowConfigDialog } from '../components/workflows/WorkflowConfigDialog';
import { WorkflowRunCard } from '../components/workflows/WorkflowRunCard';
import { WorkflowLogsViewer } from '../components/workflows/WorkflowLogsViewer';
import { ScheduleEditor } from '../components/workflows/ScheduleEditor';
import { WorkflowScheduleCard } from '../components/workflows/WorkflowScheduleCard';
import { Skeleton } from '../components/ui/Skeleton';
import { Badge } from '../components/ui/Badge';
import { PageHeader } from '../components/ui/PageHeader';
import { PillTabs, PillTabsList, PillTabsTrigger, PillTabsContent } from '../components/ui/PillTabs';
import { DropdownMenu } from '../components/ui/DropdownMenu';
import { ToastContainer } from '../components/ui/Toast';
import { InlineConfirmation } from '../components/ui/InlineConfirmation';
import { useCacheEvents } from '../hooks/useCacheEvents';
import { Input } from '../components/ui/Input';
import { usePermission } from '../hooks/usePermission';
import { useActivityHeartbeat } from '../hooks/useActivityHeartbeat';

export function Workflows() {
  const location = useLocation();
  const [activeTab, setActiveTab] = useState<'workflows' | 'runs' | 'schedules'>('workflows');
  const [workflows, setWorkflows] = useState<any[]>([]);
  const [runs, setRuns] = useState<any[]>([]);
  const [schedules, setSchedules] = useState<any[]>([]);
  const [runsCount, setRunsCount] = useState<number | null>(null);
  const [schedulesCount, setSchedulesCount] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedWorkflow, setSelectedWorkflow] = useState<any>(null);
  const [configDialogOpen, setConfigDialogOpen] = useState(false);
  const [scheduleDialogOpen, setScheduleDialogOpen] = useState(false);
  const [logsViewerOpen, setLogsViewerOpen] = useState(false);
  const [selectedRun, setSelectedRun] = useState<any>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toasts, setToasts] = useState<Array<{ id: string; message: string; type?: 'success' | 'error' | 'warning' | 'info' }>>([]);
  const [confirmDeleteRun, setConfirmDeleteRun] = useState<{ runId: string; isActive: boolean } | null>(null);
  const [confirmDeleteScheduleId, setConfirmDeleteScheduleId] = useState<string | null>(null);
  const [isDesktop, setIsDesktop] = useState<boolean>(true);
  const [runsQuery, setRunsQuery] = useState('');
  const [schedulesQuery, setSchedulesQuery] = useState('');
  const [expandedRunGroups, setExpandedRunGroups] = useState<Set<string>>(new Set());
  const [expandedScheduleGroups, setExpandedScheduleGroups] = useState<Set<string>>(new Set());
  const { isAllowed: canOpenArgoUi } = usePermission('workflows', 'open_argo_ui');
  const [argoUiUrl, setArgoUiUrl] = useState('https://argo.portal.maxiron.cloud');

  useEffect(() => {
    apiClient.getPortalConfig().then((cfg) => setArgoUiUrl(cfg.argoUiUrl)).catch(() => {});
  }, []);

  // Archive / older-runs state
  const [archiveMode, setArchiveMode] = useState(false);
  const [archivePanelOpen, setArchivePanelOpen] = useState(false);
  const [archiveFrom, setArchiveFrom] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 90);
    return d.toISOString().slice(0, 10);
  });
  const [archiveTo, setArchiveTo] = useState(() => new Date().toISOString().slice(0, 10));
  const [archiveRuns, setArchiveRuns] = useState<any[]>([]);
  const [isLoadingArchive, setIsLoadingArchive] = useState(false);

  // Mark workflows page as "active" for cache scheduler when user is watching runs.
  // This enables the backend's 15s active refresh interval for `workflow-runs` (relatedPages: ['cache:workflows']).
  useActivityHeartbeat('cache:workflows', activeTab === 'runs');

  useEffect(() => {
    loadData();
    void loadCounts();
  }, [activeTab]);

  // Handle navigation state from other pages (CI/CD events, Dev Instances)
  useEffect(() => {
    const state = location.state as any;
    if (!state) return;

    // Open a specific workflow dialog (from Dev Instances "New Instance")
    if (state.openWorkflow && !isLoading && workflows.length > 0) {
      const found = workflows.find((w: any) => w.name === state.openWorkflow);
      if (found) {
        setSelectedWorkflow(found);
        setConfigDialogOpen(true);
      }
      window.history.replaceState({}, '');
      return;
    }

    // Switch to runs tab with pre-filled search (from CI/CD events)
    if (state.openTab === 'runs') {
      setActiveTab('runs');
      if (state.runsQuery) setRunsQuery(state.runsQuery);
      window.history.replaceState({}, '');
    }
  }, [location.state, isLoading, workflows]);

  useEffect(() => {
    // Desktop-only actions (use Tailwind's md breakpoint)
    const mq = window.matchMedia('(min-width: 768px)');
    const update = () => setIsDesktop(mq.matches);
    update();
    mq.addEventListener('change', update);
    return () => mq.removeEventListener('change', update);
  }, []);

  // Listen for workflow runs cache updates via SSE
  useCacheEvents({
    enabled: true,
    cacheNames: ['workflow-runs'],
    onEvent: () => {
      // Always refresh badge count when cache changes
      void loadRunsCount();
      // Refetch runs when cache changes (background refresh)
      if (activeTab === 'runs') {
        void loadRuns();
      }
    },
  });

  const addToast = (message: string, type: 'success' | 'error' | 'warning' | 'info' = 'info') => {
    const id = Date.now().toString();
    setToasts(prev => [...prev, { id, message, type }]);
  };

  const removeToast = (id: string) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  };

  const loadData = async () => {
    setIsLoading(true);
    setError(null);
    try {
      if (activeTab === 'workflows') {
        const data = await apiClient.getWorkflows();
        // Map userAccess to access for component compatibility
        const mappedData = data.map((p: any) => ({
          ...p,
          access: {
            canExecute: p.userAccess?.allowed || false,
            requiresApproval: p.userAccess?.requiresApproval || false,
            accessLevel: p.userAccess?.accessLevel || 'none',
          },
        }));
        setWorkflows(mappedData);
      } else if (activeTab === 'runs') {
        await loadRuns();
      } else if (activeTab === 'schedules') {
        const data = await apiClient.getWorkflowSchedules();
        setSchedules(data);
      }
    } catch (err: any) {
      console.error('Failed to load data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setIsLoading(false);
    }
  };

  const loadRuns = async () => {
    try {
      const data = await apiClient.getWorkflowRuns({ limit: 50 });
      setRuns(data);
    } catch (err: any) {
      console.error('Failed to load runs:', err);
    }
  };

  const loadArchiveRuns = async () => {
    setIsLoadingArchive(true);
    try {
      const data = await apiClient.getWorkflowRunsArchive({
        from: new Date(archiveFrom).toISOString(),
        to: new Date(archiveTo + 'T23:59:59').toISOString(),
      });
      setArchiveRuns(data);
      setArchiveMode(true);
      setArchivePanelOpen(false);
    } catch (err: any) {
      console.error('Failed to load archive runs:', err);
    } finally {
      setIsLoadingArchive(false);
    }
  };

  const exitArchiveMode = () => {
    setArchiveMode(false);
    setArchiveRuns([]);
  };

  const loadRunsCount = async () => {
    try {
      const count = await apiClient.getWorkflowRunsCount();
      setRunsCount(count);
    } catch {
      // If user lacks permission or backend errors, don't show a misleading 0.
      setRunsCount(null);
    }
  };

  const loadSchedulesCount = async () => {
    try {
      const count = await apiClient.getWorkflowSchedulesCount();
      setSchedulesCount(count);
    } catch {
      setSchedulesCount(null);
    }
  };

  const loadCounts = async () => {
    await Promise.all([loadRunsCount(), loadSchedulesCount()]);
  };

  const handleTriggerWorkflow = (workflow: any) => {
    setSelectedWorkflow(workflow);
    setConfigDialogOpen(true);
  };

  const handleSubmitWorkflow = async (
    parameters: Record<string, any>,
    approvalOptions?: { notifyMode: 'select' | 'all'; selectedApprovers?: string[] }
  ) => {
    if (!selectedWorkflow) return;

    setIsSubmitting(true);
    try {
      const result = await apiClient.triggerWorkflow(
        selectedWorkflow.name, 
        parameters, 
        undefined, // tenantCode - will use default
        approvalOptions
      );
      
      if (result.status === 'pending_approval') {
        addToast(`Approval request created! Approval ID: ${result.approvalId}. Check the Approvals page to track the approval.`, 'info');
      } else {
        addToast(`Workflow started successfully! Run ID: ${result.runId}`, 'success');
        setActiveTab('runs');
        await loadRuns();
      }
      
      setConfigDialogOpen(false);
      setSelectedWorkflow(null);
    } catch (err: any) {
      addToast(`Failed to trigger workflow: ${err.message}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleScheduleWorkflow = (workflow: any) => {
    setSelectedWorkflow(workflow);
    setScheduleDialogOpen(true);
  };

  const handleSubmitSchedule = async (schedule: any) => {
    setIsSubmitting(true);
    try {
      await apiClient.createWorkflowSchedule(schedule);
      addToast('Schedule created successfully!', 'success');
      setScheduleDialogOpen(false);
      setSelectedWorkflow(null);
      setActiveTab('schedules');
      await loadData();
      await loadSchedulesCount();
    } catch (err: any) {
      addToast(`Failed to create schedule: ${err.message}`, 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleViewLogs = (run: any) => {
    setSelectedRun(run);
    setLogsViewerOpen(true);
  };

  const handleCancelRun = async (runId: string) => {
    const run = [...activeRuns, ...completedRuns].find(r => r.runId === runId);
    const isActive = run?.status === 'Pending' || run?.status === 'Running';
    
    setConfirmDeleteRun({ runId, isActive });
  };

  const confirmCancelRun = async () => {
    if (!confirmDeleteRun) return;
    
    const { runId, isActive } = confirmDeleteRun;
    
    try {
      await apiClient.cancelWorkflowRun(runId);
      const successMessage = isActive 
        ? 'Workflow run cancelled successfully!' 
        : 'Workflow run deleted successfully!';
      addToast(successMessage, 'success');
      
      // Reload runs (cache is already refreshed on backend before response)
      await loadRuns();
      await loadRunsCount();
    } catch (err: any) {
      const errorMessage = isActive 
        ? `Failed to cancel run: ${err.message}` 
        : `Failed to delete run: ${err.message}`;
      addToast(errorMessage, 'error');
    } finally {
      setConfirmDeleteRun(null);
    }
  };

  const handleDeleteSchedule = async (scheduleId: string) => {
    setConfirmDeleteScheduleId(scheduleId);
  };

  const confirmDeleteSchedule = async () => {
    if (!confirmDeleteScheduleId) return;

    try {
      await apiClient.deleteWorkflowSchedule(confirmDeleteScheduleId);
      addToast('Schedule deleted successfully!', 'success');
      await loadData();
      await loadSchedulesCount();
    } catch (err: any) {
      addToast(`Failed to delete schedule: ${err.message}`, 'error');
    } finally {
      setConfirmDeleteScheduleId(null);
    }
  };

  const handleToggleSchedule = async (schedule: any) => {
    try {
      await apiClient.updateWorkflowSchedule(schedule.id, { enabled: !schedule.enabled });
      await loadData();
      await loadSchedulesCount();
    } catch (err: any) {
      addToast(`Failed to update schedule: ${err.message}`, 'error');
    }
  };

  const handleRefresh = async () => {
    try {
      const result = await apiClient.refreshWorkflowDefinitions();
      addToast(
        `Workflow templates refreshed — ${result?.discoveredCount ?? '?'} workflows: ${(result?.workflows ?? []).join(', ')}`,
        'success',
      );
      await loadData();
    } catch (err: any) {
      addToast(`Failed to refresh: ${err.message}`, 'error');
    }
  };


  const toggleRunGroup = (key: string) => {
    setExpandedRunGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const toggleScheduleGroup = (key: string) => {
    setExpandedScheduleGroups(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const runsNeedle = useMemo(() => runsQuery.trim().toLowerCase(), [runsQuery]);
  const schedulesNeedle = useMemo(() => schedulesQuery.trim().toLowerCase(), [schedulesQuery]);

  const runMatches = (r: any) => {
    if (!runsNeedle) return true;
    const needle = runsNeedle.replace(/[_:]/g, '');
    const normalize = (s: string) => s.toLowerCase().replace(/[_:]/g, '');
    const name = normalize(String(r?.workflowName || ''));
    const triggered = normalize(String(r?.triggeredBy || ''));
    const rid = normalize(String(r?.runId || ''));
    const params = (() => {
      try { return normalize(JSON.stringify(r?.parameters || {})); } catch { return ''; }
    })();
    return name.includes(needle) || triggered.includes(needle) || rid.includes(needle) || params.includes(needle);
  };

  const scheduleMatches = (s: any) => {
    if (!schedulesNeedle) return true;
    const name = String(s?.workflowName || '').toLowerCase();
    const params = (() => {
      try { return JSON.stringify(s?.parameters || {}).toLowerCase(); } catch { return ''; }
    })();
    return name.includes(schedulesNeedle) || params.includes(schedulesNeedle);
  };

  const filteredRuns = useMemo(() => runs.filter(runMatches), [runs, runsNeedle]);
  const filteredSchedules = useMemo(() => schedules.filter(scheduleMatches), [schedules, schedulesNeedle]);

  const activeRuns = filteredRuns.filter(r => r.status === 'Running' || r.status === 'Pending');
  const completedRuns = filteredRuns.filter(r => r.status !== 'Running' && r.status !== 'Pending');

  const actionItems = [
    {
      label: 'Refresh from Git',
      icon: <RefreshCw className="w-4 h-4" />,
      onClick: handleRefresh,
    },
    isDesktop
      ? (canOpenArgoUi ? {
          label: 'Open Argo UI',
          icon: <ExternalLink className="w-4 h-4" />,
          onClick: () => window.open(argoUiUrl, '_blank'),
        } : null)
      : null,
  ].filter(Boolean) as any[];

  return (
    <div className="space-y-4">
      <PageHeader
        icon={Zap}
        iconColor="text-purple-600 dark:text-purple-400"
        iconBgColor="bg-purple-100 dark:bg-purple-900/30"
        description="Run and schedule Argo Workflows"
        count={workflows.length}
        countLabel="workflows"
        countColor="text-purple-600 dark:text-purple-400"
        actions={
          <div className="flex items-stretch w-full sm:w-auto h-full">
            <DropdownMenu
              className="h-full w-full"
              trigger={
                <Button variant="outline" className="h-full w-full gap-2">
                  <Settings className="w-4 h-4" />
                  Actions
                </Button>
              }
              items={actionItems}
            />
          </div>
        }
      />

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-3">
          <p className="text-sm text-red-800">{error}</p>
        </div>
      )}

      <PillTabs value={activeTab} onValueChange={(value) => setActiveTab(value as any)}>
        <PillTabsList>
          <PillTabsTrigger value="workflows">
            Available Workflows
            <Badge variant="outline" className="ml-2">{workflows.length}</Badge>
          </PillTabsTrigger>
          <PillTabsTrigger value="runs">
            Workflow Runs
            <Badge variant="outline" className="ml-2">{runsCount === null ? '…' : runsCount}</Badge>
          </PillTabsTrigger>
          <PillTabsTrigger value="schedules">
            Schedules
            <Badge variant="outline" className="ml-2">{schedulesCount === null ? '…' : schedulesCount}</Badge>
          </PillTabsTrigger>
        </PillTabsList>

        <PillTabsContent value="workflows" className="mt-4">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-32 w-full" />
              ))}
            </div>
          ) : workflows.length === 0 ? (
            <div className="text-center py-12 text-gray-500 dark:text-gray-400">
              No workflows available. Check your maxiron-workflows repository.
            </div>
          ) : (
            <div className="space-y-8">
              {(() => {
                const grouped = groupByCategory(workflows, (w: any) => w.name, (w: any) => w.category);
                return CATEGORY_ORDER.map((category) => {
                  const cfg = CATEGORY_CONFIG[category];
                  const CategoryIcon = cfg.icon;
                  const items = grouped.get(category) || [];
                  if (items.length === 0) return null;
                  return (
                    <div key={category}>
                      <div className={`flex items-center gap-3 pb-3 mb-4 border-b ${cfg.borderColor}`}>
                        <CategoryIcon className={`w-5 h-5 ${cfg.color}`} />
                        <h2 className={`text-xl font-bold ${cfg.color}`}>{category}</h2>
                        <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                          {items.length} workflow{items.length !== 1 ? 's' : ''}
                        </span>
                      </div>
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                        {items.map((workflow: any) => (
                          <WorkflowCard
                            key={workflow.name}
                            workflow={workflow}
                            onTrigger={() => handleTriggerWorkflow(workflow)}
                            onSchedule={() => handleScheduleWorkflow(workflow)}
                          />
                        ))}
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          )}
        </PillTabsContent>

        <PillTabsContent value="runs" className="mt-4">
              <div className="mb-4">
                <div className="relative max-w-2xl">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <Input
                    value={runsQuery}
                    onChange={(e) => setRunsQuery(e.target.value)}
                    placeholder="Search runs by workflow name or parameters..."
                    className="pl-9 pr-10"
                  />
                  {runsQuery.trim() && (
                    <button
                      type="button"
                      onClick={() => setRunsQuery('')}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      aria-label="Clear search"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
              <div className="space-y-6">
              {confirmDeleteRun && (
                <InlineConfirmation
                  message={confirmDeleteRun.isActive 
                    ? 'Are you sure you want to cancel this workflow run?' 
                    : 'Are you sure you want to delete this workflow run from history?'}
                  onConfirm={confirmCancelRun}
                  onCancel={() => setConfirmDeleteRun(null)}
                  confirmText={confirmDeleteRun.isActive ? 'Cancel Run' : 'Delete'}
                  cancelText="No, Keep It"
                />
              )}
              
              {activeRuns.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">
                    Active Runs
                    <Badge variant="outline" className="ml-2">{activeRuns.length}</Badge>
                  </h3>
                  <div className="space-y-3">
                    {activeRuns.map((run) => (
                      <WorkflowRunCard
                        key={run.runId}
                        run={run}
                        onViewLogs={() => handleViewLogs(run)}
                        onCancel={() => handleCancelRun(run.runId)}
                        compact
                      />
                    ))}
                  </div>
                </div>
              )}
              {activeRuns.length === 0 && (
                <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                  {runsNeedle ? 'No matching active runs.' : 'No active runs.'}
                </div>
              )}

              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                    {archiveMode ? 'Archive View' : 'History'}
                    <Badge variant="outline" className="ml-2">
                      {archiveMode ? archiveRuns.length : completedRuns.length}
                    </Badge>
                  </h3>
                  <div className="flex items-center gap-2">
                    {archiveMode && (
                      <span className="text-xs text-gray-500 dark:text-gray-400">
                        {archiveFrom} → {archiveTo}
                      </span>
                    )}
                    {archiveMode ? (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={exitArchiveMode}
                        className="text-xs"
                      >
                        <X className="w-3.5 h-3.5 mr-1" />
                        Back to recent
                      </Button>
                    ) : (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => setArchivePanelOpen((v) => !v)}
                        className="text-xs"
                      >
                        <Archive className="w-3.5 h-3.5 mr-1" />
                        Older runs
                      </Button>
                    )}
                  </div>
                </div>

                {archivePanelOpen && !archiveMode && (
                  <div className="mb-4 p-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 flex flex-wrap items-end gap-3">
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-gray-600 dark:text-gray-400">From</label>
                      <input
                        type="date"
                        value={archiveFrom}
                        onChange={(e) => setArchiveFrom(e.target.value)}
                        className="h-8 px-2 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                    <div className="flex flex-col gap-1">
                      <label className="text-xs font-medium text-gray-600 dark:text-gray-400">To</label>
                      <input
                        type="date"
                        value={archiveTo}
                        onChange={(e) => setArchiveTo(e.target.value)}
                        className="h-8 px-2 text-sm rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-gray-900 dark:text-gray-100"
                      />
                    </div>
                    <Button
                      size="sm"
                      onClick={loadArchiveRuns}
                      disabled={isLoadingArchive || !archiveFrom}
                    >
                      {isLoadingArchive ? 'Loading…' : 'Search'}
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => setArchivePanelOpen(false)}
                    >
                      Cancel
                    </Button>
                  </div>
                )}

                {(() => {
                  const displayRuns = archiveMode ? archiveRuns : completedRuns;
                  const emptyMsg = archiveMode
                    ? `No runs found between ${archiveFrom} and ${archiveTo}.`
                    : runsNeedle ? 'No matching completed runs.' : 'No completed runs yet.';
                  return displayRuns.length === 0 ? (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">{emptyMsg}</div>
                ) : (
                  <div className="space-y-6">
                    {(() => {
                      const groupedByCategory = groupByCategory(displayRuns, (r: any) => getRunTemplateName(r));
                      return CATEGORY_ORDER.map((category) => {
                        const cfg = CATEGORY_CONFIG[category];
                        const CategoryIcon = cfg.icon;
                        const categoryRuns = groupedByCategory.get(category) || [];
                        if (categoryRuns.length === 0) return null;

                        const byWorkflow = new Map<string, any[]>();
                        for (const run of categoryRuns) {
                          const name = getRunTemplateName(run);
                          if (!byWorkflow.has(name)) byWorkflow.set(name, []);
                          byWorkflow.get(name)!.push(run);
                        }

                        return (
                          <div key={category}>
                            <div className={`flex items-center gap-3 pb-2 mb-3 border-b ${cfg.borderColor}`}>
                              <CategoryIcon className={`w-5 h-5 ${cfg.color}`} />
                              <h4 className={`text-base font-bold ${cfg.color}`}>{category}</h4>
                              <span className="text-sm text-gray-500 dark:text-gray-400">
                                {categoryRuns.length} run{categoryRuns.length !== 1 ? 's' : ''}
                              </span>
                            </div>
                            <div className="space-y-2 md:ml-4">
                              {Array.from(byWorkflow.entries()).map(([wfName, wfRuns]) => {
                                const groupKey = `${category}:${wfName}`;
                                const isExpanded = expandedRunGroups.has(groupKey);
                                return (
                                  <div key={wfName} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                    <button
                                      type="button"
                                      onClick={() => toggleRunGroup(groupKey)}
                                      className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors text-left"
                                    >
                                      <div className="flex items-center gap-2">
                                        {isExpanded
                                          ? <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                          : <ChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-400" />}
                                        <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{wfName}</span>
                                      </div>
                                      <Badge variant="outline" className="ml-2 text-xs">{wfRuns.length}</Badge>
                                    </button>
                                    {isExpanded && (
                                      <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                        {wfRuns.map((run: any) => (
                                          <WorkflowRunCard
                                            key={run.runId}
                                            run={run}
                                            onViewLogs={() => handleViewLogs(run)}
                                            onDelete={() => handleCancelRun(run.runId)}
                                          />
                                        ))}
                                      </div>
                                    )}
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        );
                      });
                    })()}
                  </div>
                );
                })()}
              </div>
            </div>
        </PillTabsContent>

        <PillTabsContent value="schedules" className="mt-4">
          <div className="space-y-4">
              {confirmDeleteScheduleId && (
                <InlineConfirmation
                  message="Are you sure you want to delete this schedule?"
                  onConfirm={confirmDeleteSchedule}
                  onCancel={() => setConfirmDeleteScheduleId(null)}
                  confirmText="Delete Schedule"
                  cancelText="Cancel"
                />
              )}

              <div className="relative max-w-2xl">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  value={schedulesQuery}
                  onChange={(e) => setSchedulesQuery(e.target.value)}
                  placeholder="Search schedules by workflow name or parameters..."
                  className="pl-9 pr-10"
                />
                {schedulesQuery.trim() && (
                  <button
                    type="button"
                    onClick={() => setSchedulesQuery('')}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                    aria-label="Clear search"
                  >
                    <X className="w-4 h-4" />
                  </button>
                )}
              </div>
              
              {filteredSchedules.length === 0 ? (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                  {schedulesNeedle ? 'No matching schedules.' : 'No schedules configured. Create one from a workflow.'}
                </div>
              ) : (
                <div className="space-y-8">
                  {(() => {
                    const groupedByCategory = groupByCategory(filteredSchedules, (s: any) => s.workflowName);
                    return CATEGORY_ORDER.map((category) => {
                      const cfg = CATEGORY_CONFIG[category];
                      const CategoryIcon = cfg.icon;
                      const items = groupedByCategory.get(category) || [];
                      if (items.length === 0) return null;

                      const byWorkflow = new Map<string, any[]>();
                      for (const s of items) {
                        const name = s.workflowName || 'unknown';
                        if (!byWorkflow.has(name)) byWorkflow.set(name, []);
                        byWorkflow.get(name)!.push(s);
                      }

                      return (
                        <div key={category}>
                          <div className={`flex items-center gap-3 pb-3 mb-4 border-b ${cfg.borderColor}`}>
                            <CategoryIcon className={`w-5 h-5 ${cfg.color}`} />
                            <h2 className={`text-xl font-bold ${cfg.color}`}>{category}</h2>
                            <span className="text-sm font-medium text-gray-500 dark:text-gray-400">
                              {items.length} schedule{items.length !== 1 ? 's' : ''}
                            </span>
                          </div>
                          <div className="space-y-2 md:ml-4">
                            {Array.from(byWorkflow.entries()).map(([wfName, wfSchedules]) => {
                              const groupKey = `${category}:${wfName}`;
                              const isExpanded = expandedScheduleGroups.has(groupKey);
                              return (
                                <div key={wfName} className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
                                  <button
                                    type="button"
                                    onClick={() => toggleScheduleGroup(groupKey)}
                                    className="w-full flex items-center justify-between px-4 py-3 bg-gray-50 dark:bg-gray-800/50 hover:bg-gray-100 dark:hover:bg-gray-700/50 transition-colors text-left"
                                  >
                                    <div className="flex items-center gap-2">
                                      {isExpanded
                                        ? <ChevronDown className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                                        : <ChevronRight className="w-4 h-4 text-gray-500 dark:text-gray-400" />}
                                      <span className="text-sm font-medium text-gray-900 dark:text-gray-100">{wfName}</span>
                                    </div>
                                    <Badge variant="outline" className="ml-2 text-xs">{wfSchedules.length}</Badge>
                                  </button>
                                  {isExpanded && (
                                    <div className="p-3 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                                      {wfSchedules.map((schedule: any) => (
                                        <WorkflowScheduleCard
                                          key={schedule.id}
                                          schedule={schedule}
                                          onToggle={() => handleToggleSchedule(schedule)}
                                          onDelete={() => handleDeleteSchedule(schedule.id)}
                                        />
                                      ))}
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              )}
              </div>
            </PillTabsContent>
      </PillTabs>

      {selectedWorkflow && (
        <>
          <WorkflowConfigDialog
            isOpen={configDialogOpen}
            onClose={() => {
              setConfigDialogOpen(false);
              setSelectedWorkflow(null);
            }}
            workflow={selectedWorkflow}
            onSubmit={handleSubmitWorkflow}
            isSubmitting={isSubmitting}
          />
          <ScheduleEditor
            isOpen={scheduleDialogOpen}
            onClose={() => {
              setScheduleDialogOpen(false);
              setSelectedWorkflow(null);
            }}
            workflow={selectedWorkflow}
            onSubmit={handleSubmitSchedule}
            isSubmitting={isSubmitting}
          />
        </>
      )}

      {selectedRun && (
        <WorkflowLogsViewer
          isOpen={logsViewerOpen}
          onClose={() => {
            setLogsViewerOpen(false);
            setSelectedRun(null);
          }}
          runId={selectedRun.runId}
          workflowName={selectedRun.workflowName}
          status={selectedRun.status}
        />
      )}

      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </div>
  );
}
